import {
  addDoc,
  collection,
  getDocs,
  getFirestore,
  orderBy,
  query,
  updateDoc,
  where,
} from "firebase/firestore";
import { DB_NODES } from "../../../../db/constants/nodeNames";
import {
  AppNotification,
  AppNotificationType,
} from "../../models/Notification/Notification";

export interface CreateNewTradeRequestNotificationsProps {
  to: string;
  from: string;
  fromName: string;
  toName: string;
}

export interface CreateReadTradeRequestNotificationsProps {
  to: string;
  from: string;
  fromName: string;
  notificationId: string;
}

async function createNotification(notification: AppNotification) {
  const db = getFirestore();
  const notificatonsCollection = collection(db, DB_NODES.NOTIFICATIONS);

  await addDoc(notificatonsCollection, notification);
}

async function createNewTradeRequestNotifications({
  to,
  from,
  fromName,
  toName,
}: CreateNewTradeRequestNotificationsProps) {
  await createNotification({
    to,
    from,
    fromName,
    type: "trade_request_received",
    read: false,
    date: new Date(),
  });

  await createNotification({
    to: from,
    from: to,
    fromName: toName,
    type: "trade_request_sent",
    read: true,
    date: new Date(),
  });
}

async function createReadTradeRequestNotification({
  to,
  from,
  fromName,
  notificationId,
}: CreateReadTradeRequestNotificationsProps) {
  // First check that an equiavalent notification hasn't already been created.
  const db = getFirestore();
  const notificationsCollection = collection(db, DB_NODES.NOTIFICATIONS);
  const type: AppNotificationType = "trade_request_read";
  const q = query(
    notificationsCollection,
    where(DB_NODES.FROM, "==", from),
    where(DB_NODES.TYPE, "==", type),
    where(DB_NODES.NOTIFICATION_ID, "==", notificationId)
  );

  const querySnapshot = await getDocs(q);

  if (!querySnapshot.empty) return;

  // Create a new one if an equiavalent notification hasn't already been created.
  await createNotification({
    to,
    from,
    fromName,
    type: "trade_request_read",
    read: false,
    date: new Date(),
    notificationId,
  });
}

async function getUnreadNotificationsCount(userId: string): Promise<number> {
  const db = getFirestore();
  const notificationsCollection = collection(db, DB_NODES.NOTIFICATIONS);
  const q = query(
    notificationsCollection,
    where(DB_NODES.TO, "==", userId),
    where(DB_NODES.READ, "==", false)
  );
  const querySnapshot = await getDocs(q);

  return querySnapshot.size;
}

async function fetchNotifications(
  userId: string
): Promise<AppNotification[] | null> {
  let notifications: AppNotification[] | null = null;
  const db = getFirestore();
  const notificationsCollection = collection(db, DB_NODES.NOTIFICATIONS);
  const q = query(
    notificationsCollection,
    where(DB_NODES.TO, "==", userId),
    orderBy(DB_NODES.DATE, "desc")
  );

  try {
    notifications = [];
    const querySnapshot = await getDocs(q);
    querySnapshot.forEach((doc) => {
      const data = doc.data();
      notifications?.push({
        ...data,
        id: doc.id,
        date: data.date.toDate(),
      } as AppNotification);
    });
  } catch (e) {
    console.error(e);
  }

  return notifications;
}

async function readAllUnReadNotifications(userId: string) {
  const db = getFirestore();
  const notificationsCollection = collection(db, DB_NODES.NOTIFICATIONS);
  const q = query(
    notificationsCollection,
    where(DB_NODES.TO, "==", userId),
    where(DB_NODES.READ, "==", false)
  );
  const querySnapshot = await getDocs(q);

  const updates: Promise<void>[] = [];

  querySnapshot.forEach((doc) => {
    updates.push(
      updateDoc(doc.ref, {
        [DB_NODES.READ]: true,
      })
    );
  });

  await Promise.all(updates);
}

const notificationService = {
  createNewTradeRequestNotifications,
  createReadTradeRequestNotification,
  fetchNotifications,
  getUnreadNotificationsCount,
  readAllUnReadNotifications,
};

export default notificationService;
